import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import { m, useAnimationControls, useReducedMotion } from 'framer-motion'

import { FRAGMENTS, STAGE_H, STAGE_W } from './fragments'

/* ─────────────────────────────────────────────────────────────────────────
   "Crack the chaos" — the sign-in brand stage.

   The panel opens on a drifting mess of real work (tasks, a meeting, an
   invoice, a message, a sticky note, a timer) with Benny dizzy beneath it.
   Then the pieces fly into a calm, aligned grid, a dot grid settles in under
   them, the tasks tick off and the invoice is paid, and Benny cheers. It
   rests, then the day scrambles and sorts itself again.

   It runs on its own: most people sign in with a single button, so the stage
   doesn't depend on the form. Designed on a fixed stage scaled to fit the
   panel; chaos poses may spill past the edges, the grid never does.
   ───────────────────────────────────────────────────────────────────────── */

type Phase = 'chaos' | 'order'
type Mood = 'dizzy' | 'stretch' | 'happy' | 'idle'

const SPRITE: Record<Mood, string> = {
  dizzy: '/characters/animated/scrambl-dizzy.svg',
  stretch: '/characters/animated/scrambl-stretch.svg',
  happy: '/characters/animated/scrambl-happy.svg',
  idle: '/characters/animated/scrambl-idle.svg',
}
const MOODS = Object.keys(SPRITE) as Mood[]

/** Benny's rendered size on the stage; sprites are drawn on a 346 viewBox. */
const BENNY = 200
/* The sprites don't share a baseline: the dizzy puddle sits ~20 units lower
   than idle and the stretch ~11 higher. Nudge each so his contact with the
   ground never moves when the pose changes. */
const BASELINE: Record<Mood, number> = {
  dizzy: (-20 * BENNY) / 346,
  stretch: (11 * BENNY) / 346,
  happy: 0,
  idle: 0,
}

/** ms within one cycle: order lands, the work completes, then a long rest. */
const T = {
  stretch: 2200,
  order: 2700,
  happy: 3300,
  checks: [4000, 4500, 5000],
  paid: 5600,
  idle: 7000,
  cycle: 21000,
}

const EASE = [0.22, 1, 0.36, 1] as const
// Three warm layers (contact, near, ambient) — the same count in both so the
// lift-to-rest change interpolates.
const SHADOW_LIFTED =
  '0 1px 1px hsl(30 30% 18% / 0.06), 0 10px 18px -8px hsl(30 30% 18% / 0.14), 0 30px 52px -18px hsl(30 30% 18% / 0.26)'
const SHADOW_RESTING =
  '0 1px 1px hsl(30 30% 18% / 0.06), 0 3px 8px -3px hsl(30 30% 18% / 0.08), 0 14px 28px -18px hsl(30 30% 18% / 0.14)'

/** Scale the fixed stage to fit its panel (contain). */
function useStageScale(ref: RefObject<HTMLDivElement | null>) {
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width && height) setScale(Math.min(width / STAGE_W, height / STAGE_H))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])
  return scale
}

const PANEL_QUERY = '(min-width: 1024px)'

/** The panel only shows from lg up; the loop shouldn't run while it's hidden. */
function useIsPanelShown() {
  const [isShown, setIsShown] = useState(() =>
    typeof window === 'undefined'
      ? true
      : window.matchMedia(PANEL_QUERY).matches,
  )
  useEffect(() => {
    const query = window.matchMedia(PANEL_QUERY)
    const handleChange = () => setIsShown(query.matches)
    query.addEventListener('change', handleChange)
    return () => query.removeEventListener('change', handleChange)
  }, [])
  return isShown
}

function useCycle(isRunning: boolean) {
  const [phase, setPhase] = useState<Phase>('chaos')
  const [mood, setMood] = useState<Mood>('dizzy')
  const [checked, setChecked] = useState(0)
  const [isPaid, setIsPaid] = useState(false)
  // Alternates the chaos layout so each scramble lands differently.
  const [round, setRound] = useState(0)

  useEffect(() => {
    if (!isRunning) return
    const timers: number[] = []
    const at = (ms: number, fn: () => void) =>
      timers.push(window.setTimeout(fn, ms))

    function run() {
      at(T.stretch, () => setMood('stretch'))
      at(T.order, () => setPhase('order'))
      at(T.happy, () => setMood('happy'))
      T.checks.forEach((ms, i) => at(ms, () => setChecked(i + 1)))
      at(T.paid, () => setIsPaid(true))
      at(T.idle, () => setMood('idle'))
      at(T.cycle, () => {
        setPhase('chaos')
        setMood('dizzy')
        setChecked(0)
        setIsPaid(false)
        setRound(r => r + 1)
        run()
      })
    }
    run()
    return () => timers.forEach(id => window.clearTimeout(id))
  }, [isRunning])

  return { phase, mood, checked, isPaid, round }
}

/**
 * Benny changes pose the way an animator cuts between drawings: he squashes,
 * the pose swaps at the deepest point, and he springs back with a little
 * overshoot (a hop when he cheers). Every sprite stays mounted so the new
 * pose is already mid-motion rather than restarting from its first frame,
 * and the swap itself is too brief to ghost.
 */
function Benny({ mood }: { mood: Mood }) {
  const controls = useAnimationControls()
  const [shown, setShown] = useState(mood)

  useEffect(() => {
    if (mood === shown) return
    let isCancelled = false
    void (async () => {
      await controls.start({
        scaleX: 1.08,
        scaleY: 0.9,
        y: 5,
        transition: { duration: 0.14, ease: [0.55, 0, 1, 0.45] },
      })
      if (isCancelled) return
      setShown(mood)
      await controls.start({
        scaleX: 1,
        scaleY: 1,
        y: mood === 'happy' ? [5, -18, 0] : 0,
        transition: {
          scaleX: { type: 'spring', stiffness: 380, damping: 12 },
          scaleY: { type: 'spring', stiffness: 380, damping: 12 },
          y:
            mood === 'happy'
              ? { duration: 0.6, times: [0, 0.4, 1], ease: EASE }
              : { type: 'spring', stiffness: 380, damping: 16 },
        },
      })
    })()
    return () => {
      isCancelled = true
    }
  }, [mood, shown, controls])

  return (
    <m.div
      className="absolute inset-0"
      style={{ originX: 0.5, originY: 1 }}
      animate={controls}
    >
      {MOODS.map(key => (
        <img
          key={key}
          src={SPRITE[key]}
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full select-none"
          style={{
            opacity: key === shown ? 1 : 0,
            transition: 'opacity 70ms linear',
            transform: `translateY(${BASELINE[key].toFixed(1)}px)`,
          }}
        />
      ))}
    </m.div>
  )
}

/** Reduced motion: the day already sorted, Benny at rest. */
const SETTLED = {
  phase: 'order' as Phase,
  mood: 'idle' as Mood,
  checked: 3,
  isPaid: true,
  round: 0,
}

export function AuthScene() {
  const frameRef = useRef<HTMLDivElement>(null)
  const scale = useStageScale(frameRef)
  const isReduced = useReducedMotion() ?? false
  const isShown = useIsPanelShown()
  const cycle = useCycle(isShown && !isReduced)
  const { phase, mood, checked, isPaid, round } = isReduced ? SETTLED : cycle
  const isOrdered = phase === 'order'

  return (
    <div
      ref={frameRef}
      aria-hidden
      className="absolute inset-0 z-[2] flex items-center justify-center overflow-hidden"
    >
      <div
        className="relative shrink-0"
        style={{
          width: STAGE_W,
          height: STAGE_H,
          transform: `scale(${scale})`,
        }}
      >
        {/* The tray the work lands on — recessed in light mode, dots only in
            dark. It arrives with the order. */}
        <m.div
          className="absolute rounded-[28px] bg-[hsl(33_24%_85%/0.5)] shadow-[inset_0_1px_3px_hsl(30_30%_18%/0.10),0_1px_0_hsl(0_0%_100%/0.75)] [background-image:radial-gradient(hsl(30_20%_30%/0.16)_1px,transparent_1.4px)] [background-size:16px_16px] dark:bg-transparent dark:shadow-none dark:[background-image:radial-gradient(hsl(var(--foreground)/0.14)_1px,transparent_1.4px)]"
          style={{ left: 36, top: 106, width: STAGE_W - 72, height: 538 }}
          initial={false}
          animate={{ opacity: isOrdered ? 1 : 0, scale: isOrdered ? 1 : 0.96 }}
          transition={{ duration: 0.9, ease: EASE }}
        />

        {/* Benny, beneath the work */}
        <div
          className="absolute"
          style={{
            left: STAGE_W / 2 - BENNY / 2,
            top: 650,
            width: BENNY,
            height: BENNY,
          }}
        >
          <Benny mood={mood} />
        </div>

        {FRAGMENTS.map((fragment, i) => {
          const chaos = fragment.chaos[round % fragment.chaos.length]
          const pose = isOrdered
            ? {
                x: fragment.order.x,
                y: fragment.order.y,
                rotate: fragment.order.r ?? 0,
                scale: 1,
              }
            : { x: chaos.x, y: chaos.y, rotate: chaos.r, scale: chaos.s ?? 1 }
          return (
            <m.div
              key={fragment.id}
              className="absolute left-0 top-0"
              style={{
                width: fragment.w,
                height: fragment.h,
                zIndex: isOrdered ? 1 : fragment.z,
              }}
              initial={
                isReduced
                  ? false
                  : {
                      ...pose,
                      y: pose.y + 40,
                      opacity: 0,
                      scale: pose.scale * 0.9,
                    }
              }
              animate={{ ...pose, opacity: 1 }}
              transition={
                isOrdered
                  ? {
                      type: 'spring',
                      stiffness: 110,
                      damping: 17,
                      mass: 0.9,
                      delay: i * 0.055,
                    }
                  : {
                      type: 'spring',
                      stiffness: 46,
                      damping: 12,
                      delay: (FRAGMENTS.length - i) * 0.04,
                      opacity: { duration: 0.5, delay: i * 0.08 },
                    }
              }
            >
              {/* Drifts while scrambled; still once it's sorted. */}
              <m.div
                className="h-full w-full"
                animate={
                  isOrdered
                    ? { y: 0, rotate: 0 }
                    : { y: [0, -9, 0], rotate: [0, i % 2 ? 1.6 : -1.6, 0] }
                }
                transition={
                  isOrdered
                    ? { duration: 0.4 }
                    : {
                        duration: 4.6 + (i % 4) * 0.9,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: (i % 3) * 0.6,
                      }
                }
              >
                <m.div
                  className="h-full w-full rounded-[14px]"
                  initial={false}
                  animate={{
                    boxShadow: isOrdered ? SHADOW_RESTING : SHADOW_LIFTED,
                  }}
                  transition={{ duration: 0.6 }}
                >
                  {fragment.render({ checked, isPaid })}
                </m.div>
              </m.div>
            </m.div>
          )
        })}
      </div>
    </div>
  )
}
